/**
 * v3.15-1104 — CF360 Long-Open Report Page (admin/dir/mgr)
 *
 * Read-only admin report listing all clients currently dormant in CF360,
 * sorted oldest-first. Configurable threshold via env var; rows past the
 * threshold are highlighted. Per JOINT_IMPLEMENTATION_PLAN §2.5.
 */
import { useEffect, useState } from 'react';
import { Loader2, ShieldAlert, Download, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';

const ADMIN_DIR_MGR_ROLES = new Set([
  'admin', 'ptc_admin', 'ptc_director', 'ptc_manager',
  'company_director', 'company_manager',
]);

const csvEscape = (s) => {
  const v = (s ?? '').toString();
  if (/[",\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
};

export default function CF360LongOpenReportPage() {
  const { user: currentUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const canView = ADMIN_DIR_MGR_ROLES.has(currentUser?.role || '');

  useEffect(() => {
    if (!canView) return;
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const res = await api.get('/admin/cf360/long-open-report');
        if (mounted) setData(res?.data || null);
      } catch (e) {
        if (mounted) setErr(e?.response?.data?.detail || e?.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [canView]);

  const exportCsv = () => {
    if (!data?.rows?.length) return;
    const header = ['client_id', 'client_name', 'company_id', 'cf360_sent_at', 'cf360_sent_by', 'days_dormant', 'credit_concerns'];
    const lines = [header.join(',')];
    data.rows.forEach((r) => {
      lines.push([
        csvEscape(r.client_id), csvEscape(r.client_name), csvEscape(r.company_id),
        csvEscape(r.cf360_sent_at), csvEscape(r.cf360_sent_by),
        csvEscape(r.days_dormant), csvEscape(r.credit_concerns),
      ].join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cf360-long-open-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('CSV exported.');
  };

  if (!canView) {
    return (
      <div className="p-8 text-center text-slate-600" data-testid="cf360-long-open-report-forbidden">
        Access denied. Admin / Director / Manager role required.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 text-center text-slate-500" data-testid="cf360-long-open-report-loading">
        <Loader2 className="h-5 w-5 animate-spin inline mr-2" />
        Loading long-open report…
      </div>
    );
  }

  if (err) {
    return (
      <div className="p-4 rounded-md bg-rose-50 border border-rose-200 text-rose-900" data-testid="cf360-long-open-report-error">
        {err}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto" data-testid="cf360-long-open-report-page">
      <div className="flex items-start justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <ShieldAlert className="h-6 w-6 text-amber-600" />
            CF360 Long-Open Files
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Clients currently with CF360, sorted oldest-first. Long-open
            threshold: <strong>{data?.threshold_days ?? 60}</strong> days.
          </p>
        </div>
        <Button onClick={exportCsv} disabled={!data?.rows?.length} data-testid="cf360-long-open-export-csv">
          <Download className="h-4 w-4 mr-1" />
          Export CSV
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="rounded-md border bg-white p-3">
          <div className="text-xs text-slate-500 uppercase">Total dormant</div>
          <div className="text-2xl font-bold text-slate-900" data-testid="cf360-long-open-total-dormant">
            {data?.total_dormant ?? 0}
          </div>
        </div>
        <div className="rounded-md border bg-amber-50 border-amber-300 p-3">
          <div className="text-xs text-amber-800 uppercase">Over threshold</div>
          <div className="text-2xl font-bold text-amber-900" data-testid="cf360-long-open-over-threshold">
            {data?.over_threshold ?? 0}
          </div>
        </div>
        <div className="rounded-md border bg-white p-3">
          <div className="text-xs text-slate-500 uppercase">Threshold</div>
          <div className="text-2xl font-bold text-slate-900">
            {data?.threshold_days ?? 60}d
          </div>
        </div>
      </div>

      <div className="rounded-md border bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-700 text-xs uppercase">
            <tr>
              <th className="text-left px-3 py-2">Client</th>
              <th className="text-left px-3 py-2">Company</th>
              <th className="text-left px-3 py-2">Sent at</th>
              <th className="text-left px-3 py-2">Sent by</th>
              <th className="text-right px-3 py-2">Days</th>
              <th className="text-left px-3 py-2">Credit concerns</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.client_id} data-testid="cf360-long-open-row" className="border-t hover:bg-slate-50">
                <td className="px-3 py-2 font-medium text-slate-900">
                  <a href={`/clients/${r.client_id}`} className="hover:underline">
                    {r.client_name}
                  </a>
                </td>
                <td className="px-3 py-2 text-slate-600 text-xs font-mono">
                  {r.company_id?.slice(0, 8)}…
                </td>
                <td className="px-3 py-2 text-slate-600 text-xs">
                  {r.cf360_sent_at?.slice(0, 19).replace('T', ' ')}
                </td>
                <td className="px-3 py-2 text-slate-600 text-xs">
                  {r.cf360_sent_by || '—'}
                </td>
                <td className="px-3 py-2 text-right font-semibold">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ${
                    r.days_dormant >= (data?.threshold_days ?? 60)
                      ? 'bg-amber-200 text-amber-900'
                      : 'bg-slate-100 text-slate-700'
                  }`}>
                    <Clock className="h-3 w-3" />
                    {r.days_dormant}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs text-slate-700 max-w-xs truncate" title={r.credit_concerns || ''}>
                  {r.credit_concerns || '—'}
                </td>
              </tr>
            ))}
            {(!data?.rows || data.rows.length === 0) && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-slate-500">
                  No clients currently in CF360.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-400 mt-3">
        Report generated at {data?.generated_at?.slice(0, 19).replace('T', ' ')} UTC.
      </p>
    </div>
  );
}
